import React, { Component } from 'react'
import { connect } from 'react-redux'
import _ from 'lodash'
import { getAddressComponent } from './gmaps'
import DismissibleAlert from '../components/common/DismissibleAlert'

const defaultImage = {
  attributes: {
    path: '',
    caption: ''
  },
  file: null
}

export function errorClassName(error) {
  return error ? 'state-error' : ''
}

export const handleFieldChange = (component, fieldName) => event => {
  const input = event.target
  let val = input.type === 'checkbox' ? input.checked : input.value
  if (input.type === 'radio') {
    if (val === 'true') {
      val = true
    } else if (val === 'false') {
      val = false
    }
  }
  component.setState({ [fieldName]: val })
}

export const withGallery = (entitySelector, deleteImage) => ChildComponent => {
  class ComponentWithGallery extends Component {
    constructor(...args) {
      super(...args)
      this.state = { images: null, pendingDeletes: [] }
    }
    componentDidUpdate(prevProps) {
      if (prevProps.images !== this.props.images) {
        const currentImages = this.state.images || []
        this.setState({
          images: _.range(0, 5).map(i =>
            Object.assign({}, defaultImage, currentImages[i], this.props.images[i])
          )
        })
      }
    }
    render() {
      return (
        <ChildComponent
          {...this.props}
          images={this.state.images || []}
          pendingDeletes={this.state.pendingDeletes}
          clearPendingDeletes={this.clearPendingDeletes}
          handleImageSelect={this.handleImageSelect}
          handleImageRemove={this.handleImageRemove}
          handleImageCaptionChange={this.handleImageCaptionChange}
        />
      )
    }
    handleImageSelect = i => {
      return file => {
        this.setState(prev => {
          const images = prev.images ? [...prev.images] : []
          images[i] = { ...(images[i] || {}), file }
          return { images }
        })
      }
    }
    handleImageRemove = (entity, i) => {
      return event => {
        event.preventDefault()
        const image = this.state.images[i]
        if (!image || !image.id) {
          // Newly selected (not yet saved) image — remove from UI only
          this.setState({
            images: this.state.images.filter((_, index) => index !== i)
          })
          return
        }
        // Existing server image — queue for bulk delete on form submit
        this.setState(prev => ({
          images: prev.images.filter((_, index) => index !== i),
          pendingDeletes: [...prev.pendingDeletes, image]
        }))
      }
    }
    clearPendingDeletes = () => {
      this.setState({ pendingDeletes: [] })
    }
    handleImageCaptionChange = i => {
      return caption => {
        this.setState(prev => {
          const images = prev.images ? [...prev.images] : []
          images[i] = {
            ...(images[i] || {}),
            attributes: { ...((images[i] || {}).attributes || {}), caption }
          }
          return { images }
        })
      }
    }
  }

  const mapStateToProps = state => {
    const entity = entitySelector(state)
    let images
    if (entity) {
      images = entity.getRelated('images')
    }
    // Fallback: when the API stores images as a flat imageUrls array on attributes
    // (instead of a JSON:API relationship), synthesise image objects from those URLs.
    if (!images || images.size() === 0) {
      const urls = entity && entity.get('imageUrls')
      if (urls && urls.length > 0) {
        return {
          images: urls.map(url => ({
            id: null,
            type: 'image',
            attributes: { url, path: '', caption: '' }
          }))
        }
      }
    }
    // Fallback: galleryImages array [{id, url}] returned by newer API endpoints
    if (!images || images.size() === 0) {
      const galleryImgs = entity && entity.get('galleryImages')
      if (galleryImgs && galleryImgs.length > 0) {
        return {
          images: galleryImgs.map(img => ({
            id: img.id,
            type: 'image',
            attributes: { url: img.url, path: '', caption: '' }
          }))
        }
      }
    }
    return {
      images: images ? images.toJSON() : []
    }
  }

  return connect(mapStateToProps, { deleteImage })(ComponentWithGallery)
}

export const withFileInput = ChildComponent => {
  class ComponentWithFileInput extends Component {
    constructor(props) {
      super(props)
      this.state = {
        fileName: '',
        previewUrl: null
      }
      this._objectUrl = null
    }
    componentWillUnmount() {
      if (this._objectUrl) {
        URL.revokeObjectURL(this._objectUrl)
        this._objectUrl = null
      }
    }
    render() {
      return (
        <ChildComponent
          {...this.props}
          fileName={this.state.fileName}
          previewUrl={this.state.previewUrl}
          handleFileInputChange={this.handleFileInputChange}
        />
      )
    }
    handleFileInputChange = event => {
      const fileInput = event.target
      const file = fileInput.files[0]
      const fileName = fileInput.value.replace(/.+\\/g, '')
      if (this._objectUrl) {
        URL.revokeObjectURL(this._objectUrl)
        this._objectUrl = null
      }
      let previewUrl = null
      if (file && file.type.startsWith('image/')) {
        previewUrl = URL.createObjectURL(file)
        this._objectUrl = previewUrl
      }
      this.setState({ fileName, previewUrl })
      if (this.props.onFileSelect) {
        this.props.onFileSelect({
          target: {
            type: 'syntheticTarget',
            value: {
              fileName,
              file: fileInput.files[0]
            }
          }
        })
      }
    }
  }

  return ComponentWithFileInput
}

export const withGeoSuggestInput = ChildComponent => {
  class ComponentWithGeoSuggestInput extends Component {
    render() {
      return (
        <ChildComponent
          {...this.props}
          onGeoInputChange={this.handleInputChange}
          onGeoSuggestSelect={this.handleSuggestSelect}
        />
      )
    }
    handleInputChange = value => {
      const { onGeoInputChange, onGeoSuggestSelect } = this.props
      if (onGeoInputChange) {
        onGeoInputChange({
          target: {
            type: 'text',
            value
          }
        })
      }
      if (onGeoSuggestSelect) {
        onGeoSuggestSelect({
          target: {
            type: 'syntheticTarget',
            value: null
          }
        })
      }
    }
    handleSuggestSelect = suggest => {
      const { onGeoSuggestSelect } = this.props
      if (!onGeoSuggestSelect) {
        return
      }
      const addrComponent = getAddressComponent.bind(null, suggest.gmaps)
      onGeoSuggestSelect({
        target: {
          type: 'syntheticTarget',
          value: {
            storeAddress: suggest.label,
            storeLat: suggest.location.lat,
            storeLng: suggest.location.lng,
            gmapStreetAddr: suggest.gmaps.formatted_address,
            gmapRoute: addrComponent('route'),
            gmapIntersection: addrComponent('intersection'),
            gmapPolitical: addrComponent('political'),
            gmapCountry: addrComponent('country'),
            gmapAdminAreaLvl1: addrComponent('administrative_area_level_1'),
            gmapAdminAreaLvl2: addrComponent('administrative_area_level_2'),
            gmapAdminAreaLvl3: addrComponent('administrative_area_level_3'),
            gmapAdminAreaLvl4: addrComponent('administrative_area_level_4'),
            gmapAdminAreaLvl5: addrComponent('administrative_area_level_5'),
            gmapColloquialArea: addrComponent('colloquial_area'),
            gmapLocality: addrComponent('locality'),
            gmapSublocality: addrComponent('sublocality'),
            gmapSublocality1: addrComponent('sublocality_level_1'),
            gmapSublocality2: addrComponent('sublocality_level_2'),
            gmapSublocality3: addrComponent('sublocality_level_3'),
            gmapSublocality4: addrComponent('sublocality_level_4'),
            gmapSublocality5: addrComponent('sublocality_level_5'),
            gmapNeighborhood: addrComponent('neighborhood'),
            gmapPremise: addrComponent('premise'),
            gmapSubPremise: addrComponent('subpremise'),
            gmapPostalCode: addrComponent('postal_code'),
            gmapNaturalFeature: addrComponent('natural_feature'),
            gmapAirport: addrComponent('airport'),
            gmapPark: addrComponent('park')
          }
        }
      })
    }
  }

  return ComponentWithGeoSuggestInput
}

export class FormErrorMessage extends Component {
  render() {
    const { error, onRef, errorListClassName } = this.props
    if (!error || _.isEmpty(error)) {
      return null
    }
    return (
      <DismissibleAlert type="danger" onRef={onRef} resetKey={error}>
        {error.type === 'validation'
          ? <div>
              <span>Please correct the following and submit again:</span>
              {this.renderValidationErrors(error, errorListClassName)}
            </div>
          : <div>
              <span>{error.message || 'Something went wrong, please try again.'}</span>
              {this.renderErrorList(error.errors, errorListClassName)}
            </div>}
      </DismissibleAlert>
    )
  }
  renderValidationErrors = (error, errorListClassName) => {
    const messages = Object.entries(error)
      .filter(([key]) => key !== 'type')
      .map(([, msg]) => msg)
      .filter(Boolean)
    if (messages.length === 0) return null
    return (
      <ul className={errorListClassName}>
        {messages.map(msg => <li key={msg}>{msg}</li>)}
      </ul>
    )
  }
  renderErrorList = (errors, errorListClassName) => {
    if (!errors || errors.length === 0) return null
    return (
      <ul className={errorListClassName}>
        {errors.map(msg => <li key={msg}>{msg}</li>)}
      </ul>
    )
  }
}
